import { NAVIGATION_REGISTRY } from '@/app/lib/search/navigationRegistry';
import type { NavEntry } from '@/app/lib/search/types';

export type NavAreaId = 'home' | 'plan' | 'find' | 'campus' | 'you';

/**
 * The shell groups every registered destination into five areas. The rail picks
 * an area and the panel lists that area's tools, so no destination sits more
 * than two taps away and no list runs past one screen.
 */
export const NAV_AREAS: Array<{ id: NavAreaId; label: string; title: string; iconName: string; ids: string[] }> = [
  { id: 'home', label: 'Home', title: 'Home', iconName: 'HomeIcon', ids: ['home', 'ai-assistant', 'help', 'rutgers-planning-guides', 'support'] },
  { id: 'plan', label: 'Plan', title: 'Plan', iconName: 'CalendarDaysIcon', ids: ['scheduling', 'degree-planning', 'course-sniper', 'course-lists', 'plan', 'prerequisite-checker', 'gpa-calculator'] },
  { id: 'find', label: 'Find', title: 'Find', iconName: 'MagnifyingGlassIcon', ids: ['explore', 'course-wiki', 'sirs', 'degree-search', 'research-opportunities', 'research-outreach', 'ontology'] },
  { id: 'campus', label: 'Campus', title: 'Campus', iconName: 'MapPinIcon', ids: ['campus', 'bus-tracker', 'dining', 'parking-map', 'room-availability', 'classroom-guide', 'events', 'class-chat', 'info-rutgers'] },
  { id: 'you', label: 'You', title: 'You', iconName: 'UserIcon', ids: ['profile', 'profile-schedules', 'profile-degree-plans', 'profile-course-lists', 'profile-previous-classes', 'friends', 'profile-memories', 'optimal-setup', 'design-system'] },
];

const ENTRY_BY_ID = new Map(NAVIGATION_REGISTRY.map(entry => [entry.id, entry]));

// Detail pages reached from an area's tools rather than listed in the registry.
const EXTRA_PREFIXES: Array<[string, NavAreaId]> = [['/professors', 'find'], ['/programs', 'find'], ['/degrees', 'find'], ['/course-list', 'plan'], ['/map', 'campus'], ['/finals', 'plan'], ['/classes-by-time', 'find'], ['/classes-on-map', 'campus'], ['/guides', 'home'], ['/support', 'home']];

export function navAreaItems(area: NavAreaId): NavEntry[] {
  return NAV_AREAS.find(entry => entry.id === area)!.ids.map(id => ENTRY_BY_ID.get(id)).filter((entry): entry is NavEntry => Boolean(entry));
}

/** The area that owns the current page, by longest matching destination path. */
export function navAreaForPath(pathname: string): NavAreaId {
  let best: { area: NavAreaId; length: number } = { area: 'home', length: 0 };
  for (const area of NAV_AREAS) {
    for (const id of area.ids) {
      const path = ENTRY_BY_ID.get(id)?.href.split('?')[0];
      if (!path || path === '/') continue;
      if ((pathname === path || pathname.startsWith(`${path}/`)) && path.length > best.length) best = { area: area.id, length: path.length };
    }
  }
  for (const [path, area] of EXTRA_PREFIXES) {
    if ((pathname === path || pathname.startsWith(`${path}/`)) && path.length > best.length) best = { area, length: path.length };
  }
  return best.area;
}
